import { format, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'

export function formatDate(dateString: string, formatString: string = 'dd.MM.yyyy'): string {
  if (!dateString) return '-'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, formatString, { locale: pl })
  } catch {
    return dateString
  }
}

export function formatDateTime(dateString: string): string {
  if (!dateString) return '-'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'dd.MM.yyyy HH:mm', { locale: pl })
  } catch {
    return dateString
  }
}

export function formatCurrency(amount: number, currency: string = 'PLN'): string {
  if (amount === null || amount === undefined) return '-'
  try {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  } catch (e) {
    // Fallback if currency code is invalid
    return `${amount} ${currency}`
  }
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return '-'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export function truncateText(text: string, maxLength: number = 80): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function getNoticeTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    ContractNotice: 'Ogłoszenie o zamówieniu (ContractNotice)',
    TenderResultNotice: 'Ogłoszenie o wynikach postępowania (TenderResultNotice)',
    AgreementIntentionNotice: 'Ogłoszenie o zamiarze zawarcia umowy (AgreementIntentionNotice)',
    CompetitionNotice: 'Ogłoszenie o konkursie (CompetitionNotice)',
    CompetitionResultNotice: 'Ogłoszenie o wyniku konkursu (CompetitionResultNotice)',
    NoticeUpdateNotice: 'Ogłoszenie o aktualizacji ogłoszenia (NoticeUpdateNotice)',
    AgreementUpdateNotice: 'Ogłoszenie o aktualizacji umowy (AgreementUpdateNotice)',
    ContractPerformingNotice: 'Ogłoszenie o wykonaniu umowy (ContractPerformingNotice)',
    CircumstancesFulfillmentNotice:
      'Ogłoszenie o spełnieniu okoliczności (CircumstancesFulfillmentNotice)',
    SmallContractNotice: 'Ogłoszenie o małym zamówieniu (SmallContractNotice)',
    ConcessionNotice: 'Ogłoszenie koncesyjne (ConcessionNotice)',
    ConcessionIntentionAgreementNotice:
      'Ogłoszenie o zamiarze umowy koncesyjnej (ConcessionIntentionAgreementNotice)',
    NoticeUpdateConcession: 'Aktualizacja ogłoszenia koncesyjnego (NoticeUpdateConcession)',
    ConcessionAgreementNotice: 'Ogłoszenie o umowie koncesyjnej (ConcessionAgreementNotice)',
    ConcessionUpdateAgreementNotice:
      'Aktualizacja umowy koncesyjnej (ConcessionUpdateAgreementNotice)',
  }
  return typeLabels[type] || type || 'Nieznany typ'
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: 'text-success bg-success/10',
    failed: 'text-danger bg-danger/10',
    running: 'text-brand bg-brand-bg',
  }
  return colors[status] || 'text-ink-muted bg-surface-inset'
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    completed: 'Ukończono',
    failed: 'Błąd',
    running: 'W trakcie',
  }
  return labels[status] || status || 'Nieznany'
}

// Safely decode HTML entities (e.g. &#34; -> ") for text coming from API/database
// Uses the browser to handle named/numeric entities without introducing XSS
export function decodeHtmlEntities(text: string): string {
  if (!text) return ''
  try {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  } catch {
    // Fallback: basic named and numeric entity decoding
    return text
      .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/"/g, '"')
      .replace(/'/g, "'")
      .replace(/&/g, '&')
      .replace(/</g, '<')
      .replace(/>/g, '>')
  }
}
